// Coletor Web Bluetooth.
//
// Roda no navegador do tablet do quarto — Web Bluetooth nao existe no servidor,
// entao e este arquivo que faz a ponte com os aparelhos (docs/PRODUCT.md §7.1).
//
// O que ele precisa aguentar: ficar ligado a noite inteira, reconectar sozinho
// quando o aparelho volta, e nunca perder uma medida por falta de rede — quem
// mediu ja gastou a fita e ja furou o dedo.

import {
  LINKSUN_SERVICO, LINKSUN_CARACTERISTICA,
  YK_SERVICO, YK_CARACTERISTICA,
  FC_SERVICO, FC_CARACTERISTICA,
  FUSO,
} from '@medicov1/shared';
import {
  traduzirLinkSun, traduzirYk, traduzirFc, guardarDesvio, lerDesvio,
  type LeituraParaEnviar, type EstadoFc,
} from './traduzir';

export type TipoDeAparelho = 'glicosimetro' | 'pressao' | 'cardio';
export type EstadoConexao = 'desconhecido' | 'procurando' | 'conectando' | 'conectado' | 'desconectado';

export interface PerfilAparelho {
  tipo: TipoDeAparelho;
  rotulo: string;
  /** Prefixo do nome anunciado, para o seletor mostrar so o aparelho certo. */
  prefixoNome?: string;
  servico: number | string;
  caracteristica: number | string;
  /** Todos os UUIDs que o Chrome precisa conhecer para entregar o servico. */
  opcionais: (number | string)[];
}

export const PERFIS: Record<TipoDeAparelho, PerfilAparelho> = {
  glicosimetro: {
    tipo: 'glicosimetro', rotulo: 'Glicosímetro LinkSun', prefixoNome: 'LinkSun',
    servico: LINKSUN_SERVICO, caracteristica: LINKSUN_CARACTERISTICA,
    opcionais: [LINKSUN_SERVICO, 0x180a, 0x180f],
  },
  pressao: {
    tipo: 'pressao', rotulo: 'Pressão YK-IBPA1', prefixoNome: 'YK-',
    servico: YK_SERVICO, caracteristica: YK_CARACTERISTICA,
    opcionais: [YK_SERVICO, 0x180a, 0x180f, 0x1804],
  },
  cardio: {
    // Implementado as cegas pelo perfil padrao: o Coospo HW9 nunca apareceu em
    // scan nenhum na Etapa 0. Se ele seguir a norma, funciona de primeira.
    tipo: 'cardio', rotulo: 'Cardio (perfil padrão)',
    servico: FC_SERVICO, caracteristica: FC_CARACTERISTICA,
    opcionais: [FC_SERVICO, 0x180a, 0x180f],
  },
};

export interface StatusAparelho {
  tipo: TipoDeAparelho;
  rotulo: string;
  estado: EstadoConexao;
  nome?: string;
  ultimoDadoEm?: number;
  mensagem?: string;
}

interface Ligacao {
  dispositivo: BluetoothDevice;
  caracteristica?: BluetoothRemoteGATTCharacteristic;
  estadoFc: EstadoFc;
  /** Desvio do relogio daquele aparelho, aprendido na primeira medida do momento. */
  desvioRelogio: number | null;
  /** Ultimos quadros crus vistos, para barrar reentrega imediata. */
  quadrosRecentes: string[];
}

export class Coletor {
  private ligacoes = new Map<TipoDeAparelho, Ligacao>();
  private status = new Map<TipoDeAparelho, StatusAparelho>();
  /** Aparelhos com uma tentativa de conexao em andamento. */
  private tentando = new Set<TipoDeAparelho>();
  private desligado = false;

  constructor(
    private aoReceber: (tipo: TipoDeAparelho, leituras: LeituraParaEnviar[]) => void,
    private aoMudarStatus: (status: StatusAparelho[]) => void,
  ) {
    for (const perfil of Object.values(PERFIS)) {
      this.status.set(perfil.tipo, { tipo: perfil.tipo, rotulo: perfil.rotulo, estado: 'desconhecido' });
    }
  }

  static suportado(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.bluetooth;
  }

  /**
   * A funcao getDevices() EXISTIR nao garante que a permissao sobreviva a um
   * recarregamento — verificado no Redmi em 16/09/2026: getDevices respondia,
   * mas voltava vazia depois do reload. So a flag
   * chrome://flags/#enable-web-bluetooth-new-permissions-backend faz a
   * permissao persistir. Por isso a checagem de verdade e perguntar se ha
   * aparelho lembrado, nao se a API existe.
   */
  static temGetDevices(): boolean {
    return Coletor.suportado() && typeof navigator.bluetooth.getDevices === 'function';
  }

  /** Quantos aparelhos o navegador REALMENTE lembra agora. */
  static async quantosLembrados(): Promise<number> {
    if (!Coletor.temGetDevices()) return 0;
    try {
      return (await navigator.bluetooth.getDevices()).length;
    } catch {
      return 0;
    }
  }

  listarStatus(): StatusAparelho[] {
    return [...this.status.values()];
  }

  private mudar(tipo: TipoDeAparelho, mudanca: Partial<StatusAparelho>) {
    const atual = this.status.get(tipo)!;
    this.status.set(tipo, { ...atual, ...mudanca });
    this.aoMudarStatus(this.listarStatus());
  }

  /**
   * Pareamento: precisa de gesto humano, e o Chrome so mostra o seletor a partir
   * de um clique. Acontece uma vez por aparelho.
   */
  async parear(tipo: TipoDeAparelho): Promise<void> {
    const perfil = PERFIS[tipo];
    this.mudar(tipo, { estado: 'procurando', mensagem: undefined });

    try {
      const dispositivo = await navigator.bluetooth.requestDevice({
        filters: perfil.prefixoNome
          ? [{ namePrefix: perfil.prefixoNome }]
          : [{ services: [perfil.servico] }],
        optionalServices: perfil.opcionais,
      });
      await this.ligar(tipo, dispositivo);
    } catch (erro) {
      const msg = erro instanceof Error ? erro.message : 'falhou';
      // Cancelar o seletor e acao do usuario, nao defeito.
      const cancelou = /cancel/i.test(msg);
      this.mudar(tipo, { estado: 'desconhecido', mensagem: cancelou ? undefined : msg });
    }
  }

  /**
   * Reconecta sozinho aos aparelhos ja autorizados, sem ninguem tocar na tela.
   *
   * Esperar o aparelho ANUNCIAR para so entao conectar parecia o caminho certo,
   * e e o que a documentacao sugere — mas medido com o YK-IBPA1 levava cerca de
   * DOIS MINUTOS. Esse aparelho se desliga sozinho e ja comeca a medir quando
   * religa: quando a conexao enfim vinha, a medicao ja tinha passado, e ele nao
   * reenvia o que considera entregue. A medida se perdia toda vez.
   *
   * Agora a conexao e pedida direto e fica PENDENTE ate o aparelho aparecer —
   * assim o notify ja esta assinado no instante em que ele liga.
   */
  async reconectarConhecidos(): Promise<void> {
    if (!Coletor.temGetDevices()) return;

    const lembrados = await navigator.bluetooth.getDevices();
    for (const dispositivo of lembrados) {
      const tipo = this.adivinharTipo(dispositivo.name ?? '');
      if (!tipo || this.ligacoes.get(tipo)?.dispositivo.gatt?.connected) continue;
      void this.insistirEmConectar(tipo, dispositivo);
    }
  }

  /**
   * Fica tentando ate conseguir. Uma tentativa por aparelho de cada vez: chamar
   * connect() em paralelo trava a fila de GATT do Chrome.
   */
  private async insistirEmConectar(tipo: TipoDeAparelho, dispositivo: BluetoothDevice): Promise<void> {
    if (this.tentando.has(tipo)) return;
    this.tentando.add(tipo);
    this.mudar(tipo, { estado: 'procurando', nome: dispositivo.name ?? undefined });

    try {
      while (!this.desligado && !dispositivo.gatt?.connected) {
        // `silencioso`: as tentativas nao mexem no estado da tela. Sem isso o
        // card pisca entre "conectando" e "procurando" a cada tentativa, e quem
        // olha o monitor acha que a conexao esta instavel.
        const ok = await this.ligar(tipo, dispositivo, true);
        if (ok) return;
        // 2 s, nao 4: o glicosimetro fica visivel por MUITO menos tempo que o
        // monitor de pressao — ele para de anunciar assim que comeca a medir, e
        // a medicao dura poucos segundos. Cada tentativa perdida e uma fita.
        await new Promise((r) => setTimeout(r, 2000));
      }
    } finally {
      this.tentando.delete(tipo);
    }
  }

  private adivinharTipo(nome: string): TipoDeAparelho | null {
    if (/linksun/i.test(nome)) return 'glicosimetro';
    if (/^yk-/i.test(nome)) return 'pressao';
    if (/hw9|coospo/i.test(nome)) return 'cardio';
    return null;
  }

  /** Devolve `true` quando conectou e o notify ficou assinado. */
  private async ligar(
    tipo: TipoDeAparelho,
    dispositivo: BluetoothDevice,
    silencioso = false,
  ): Promise<boolean> {
    const perfil = PERFIS[tipo];
    if (!silencioso) this.mudar(tipo, { estado: 'conectando', nome: dispositivo.name ?? undefined });

    try {
      const servidor = await dispositivo.gatt!.connect();
      const servico = await servidor.getPrimaryService(perfil.servico);
      const caracteristica = await servico.getCharacteristic(perfil.caracteristica);

      caracteristica.addEventListener('characteristicvaluechanged', (evento) => {
        this.receber(tipo, (evento.target as BluetoothRemoteGATTCharacteristic).value!);
      });
      await caracteristica.startNotifications();

      dispositivo.addEventListener('gattserverdisconnected', () => {
        this.mudar(tipo, { estado: 'desconectado' });
        // Aparelho desligado nao e erro, e rotina: volta a insistir na hora, para
        // o notify ja estar assinado quando ele religar e comecar a medir.
        void this.insistirEmConectar(tipo, dispositivo);
      }, { once: true });

      const anterior = this.ligacoes.get(tipo);
      this.ligacoes.set(tipo, {
        dispositivo, caracteristica,
        estadoFc: anterior?.estadoFc ?? { ultimoValor: null, ultimoEnvioMs: 0 },
        // O desvio e do relogio do aparelho, nao da sessao: sobrevive a
        // reconexao, a recarregar a pagina e a esquecer o aparelho.
        desvioRelogio: anterior?.desvioRelogio ?? lerDesvio(tipo),
        quadrosRecentes: anterior?.quadrosRecentes ?? [],
      });
      this.mudar(tipo, { estado: 'conectado', mensagem: undefined });
      return true;
    } catch (erro) {
      // Aparelho fora de alcance cai aqui o tempo todo enquanto esta desligado.
      // Nao e falha para mostrar ao usuario — e a espera normal.
      if (!silencioso) this.mudar(tipo, { estado: 'procurando' });
      if (import.meta.env.DEV) {
        const registro = globalThis as unknown as { __tentativas?: unknown[] };
        registro.__tentativas ??= [];
        registro.__tentativas.push({
          tipo, hora: new Date().toLocaleTimeString('pt-BR', { timeZone: FUSO }),
          erro: erro instanceof Error ? erro.message : String(erro),
        });
      }
      return false;
    }
  }

  private receber(tipo: TipoDeAparelho, valor: DataView): void {
    const ligacao = this.ligacoes.get(tipo);
    if (!ligacao) return;

    const agora = new Date();
    const bytes = new Uint8Array(valor.buffer, valor.byteOffset, valor.byteLength);
    const cru = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
    let leituras: LeituraParaEnviar[] = [];

    // O LinkSun reentrega o mesmo quadro com milissegundos de diferenca, e isso
    // gravava a mesma glicemia duas vezes. Quadro identico ja visto nesta
    // conexao nao passa. Quadros do manguito (0x80) repetem valores por
    // natureza e ficam de fora desta guarda.
    if (bytes[0] !== 0x80) {
      if (ligacao.quadrosRecentes.includes(cru)) return;
      ligacao.quadrosRecentes.push(cru);
      if (ligacao.quadrosRecentes.length > 16) ligacao.quadrosRecentes.shift();
    }

    // Em desenvolvimento guarda o quadro cru. Quando o aparelho manda algo que o
    // sistema descarta, e a unica forma de saber o que chegou de verdade.
    if (import.meta.env.DEV) {
      const registro = globalThis as unknown as { __quadros?: unknown[] };
      registro.__quadros ??= [];
      registro.__quadros.push({
        tipo,
        hora: agora.toLocaleTimeString('pt-BR', { timeZone: FUSO }),
        hex: Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join(' '),
      });
    }

    if (tipo === 'glicosimetro') {
      const l = traduzirLinkSun(bytes, agora);
      if (l) leituras = [l];
    } else if (tipo === 'pressao') {
      const r = traduzirYk(bytes, agora, ligacao.desvioRelogio);
      if (r.desvioAprendido !== null) {
        ligacao.desvioRelogio = r.desvioAprendido;
        guardarDesvio(tipo, r.desvioAprendido);
      }
      leituras = r.leituras;
    } else {
      const r = traduzirFc(valor, ligacao.estadoFc, agora);
      ligacao.estadoFc = r.estado;
      if (r.leitura) leituras = [r.leitura];
    }

    this.mudar(tipo, { ultimoDadoEm: agora.getTime() });
    if (leituras.length) this.aoReceber(tipo, leituras);
  }

  desligar(tipo: TipoDeAparelho): void {
    const ligacao = this.ligacoes.get(tipo);
    if (ligacao?.dispositivo.gatt?.connected) ligacao.dispositivo.gatt.disconnect();
    this.ligacoes.delete(tipo);
    this.tentando.delete(tipo);
    this.mudar(tipo, { estado: 'desconhecido' });
  }

  desligarTudo(): void {
    this.desligado = true;
    for (const tipo of [...this.ligacoes.keys()]) this.desligar(tipo);
  }
}
